import axios from 'axios';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  ProjectArchiveJob,
  ProjectRestoreJob,
} from '../../../../../../typings/api/skymap/rest/v1/project';
import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { isDefined } from '../../../../utils/variables';
import { useNonce } from '../../../hooks/use-nonce';
import { AppContext, UserRole } from '../../../state/app-state';
import { ProjectContext } from '../../../state/project-state/project-state';
import { Button } from '../../button/button';
import { InfoBox } from '../../info-box/info-box';
import { SectionTitle } from '../styles/styles';
import { ActiveInfoBox } from './active-info-box';
import { ArchiveInfoBox } from './archive-info-box';
import { InactiveInfoBox } from './inactive-info-box';
import { RestoreInfoBox } from './restore-info-box';
import { Flex } from './styles';
import { ValidToDateSection } from './valid-to-date-section';

enum FetchJobStatus {
  Loading,
  Success,
  Error,
}

const Status = () => {
  const { t } = useTranslation();
  const { currentUser, isOrganizationAdmin } = React.useContext(AppContext);
  const { project } = React.useContext(ProjectContext);

  const [retryNonce, updateRetryNonce] = useNonce();
  const [fetchJobStatus, setFetchJobStatus] = React.useState(FetchJobStatus.Loading);
  const [latestArchiveJob, setLatestArchiveJob] = React.useState<ProjectArchiveJob>();
  const [latestRestoreJob, setLatestRestoreJob] = React.useState<ProjectRestoreJob>();

  React.useEffect(() => {
    const cancelTokenSource = axios.CancelToken.source();

    const fetch = async () => {
      const [archiveJobsResp, restoreJobsResp] = await Promise.allSettled([
        SkyMapAxiosServiceFactory.instance.createProjectServiceV1().getArchiveJobs({
          path: {
            projectId: project.id,
          },
          query: {
            include: ['projectArchiveJob.bucketJobs'],
            orderBy: ['projectArchiveJob.createdAt:desc'],
          },
        }),
        SkyMapAxiosServiceFactory.instance.createProjectServiceV1().getRestoreJobs({
          path: {
            projectId: project.id,
          },
          query: {
            include: [
              'projectRestoreJob.bucketJobs',
              'projectRestoreJob.bucketJobs.projectArchiveBucketJob',
            ],
            orderBy: ['projectRestoreJob.createdAt:desc'],
          },
        }),
      ]);

      const isFulfilled = <T,>(
        input: PromiseSettledResult<T>,
      ): input is PromiseFulfilledResult<T> => input.status === 'fulfilled';

      const isError404 = <T,>(input: PromiseSettledResult<T>) =>
        input.status === 'rejected' && input.reason.response?.status === 404;

      if (!isFulfilled(archiveJobsResp) && !isError404(archiveJobsResp)) {
        setFetchJobStatus(FetchJobStatus.Error);
      } else if (!isFulfilled(restoreJobsResp) && !isError404(restoreJobsResp)) {
        setFetchJobStatus(FetchJobStatus.Error);
      } else {
        setFetchJobStatus(FetchJobStatus.Success);

        if (isFulfilled(archiveJobsResp)) {
          setLatestArchiveJob(archiveJobsResp.value.data[0]);
        }

        if (isFulfilled(restoreJobsResp)) {
          setLatestRestoreJob(restoreJobsResp.value.data[0]);
        }
      }
    };

    if (isOrganizationAdmin) {
      void fetch();
    }

    return () => {
      cancelTokenSource.cancel();
    };
  }, [project, retryNonce, isOrganizationAdmin]);

  const isArchiveJobNewerThanRestoreJob = (job?: ProjectArchiveJob): job is ProjectArchiveJob =>
    job && latestRestoreJob
      ? new Date(job.createdAt).getTime() > new Date(latestRestoreJob.createdAt).getTime()
      : isDefined(job);

  const isRestoreJobNewerThanArchiveJob = (job?: ProjectRestoreJob): job is ProjectRestoreJob =>
    job && latestArchiveJob
      ? new Date(job.createdAt).getTime() > new Date(latestArchiveJob.createdAt).getTime()
      : isDefined(job);

  const retryButton = (
    <Button
      color="primary"
      noWrap={true}
      variant="contained"
      onClick={() => {
        updateRetryNonce();
      }}
    >
      {t('tryAgain', { ns: 'common' })}
    </Button>
  );

  const renderActiveInfoBox = () => {
    return <ActiveInfoBox />;
  };

  const renderDisabledInfoBox = () => {
    switch (fetchJobStatus) {
      case FetchJobStatus.Loading:
        return (
          <InfoBox
            bottomMargin={true}
            color="yellow"
            leftIcon={{ icon: ['fad', 'spinner'], spin: true }}
          >
            {t('oneMoment', { ns: 'common' })}
          </InfoBox>
        );
      case FetchJobStatus.Error:
        return (
          <InfoBox bottomMargin={true} color="red" leftIcon={{ icon: ['fad', 'lock-alt'] }}>
            <Flex>
              <div>{t('projectSettings.status.fetchError', { ns: 'components' })}</div>
              {retryButton}
            </Flex>
          </InfoBox>
        );
      case FetchJobStatus.Success: {
        if (isArchiveJobNewerThanRestoreJob(latestArchiveJob)) {
          return <ArchiveInfoBox job={latestArchiveJob} />;
        }

        if (
          isRestoreJobNewerThanArchiveJob(latestRestoreJob) &&
          latestRestoreJob.status !== 'DONE'
        ) {
          return <RestoreInfoBox job={latestRestoreJob} />;
        }

        return <InactiveInfoBox />;
      }
    }
  };

  return (
    <Component>
      <SectionTitle>
        {t('projectSettings.status.sectionTitle', { ns: 'components' }).toUpperCase()}
      </SectionTitle>

      {project.status === 'ACTIVE' ? renderActiveInfoBox() : renderDisabledInfoBox()}

      {currentUser?.selectedRole === UserRole.OrganizationAdmin && <ValidToDateSection />}
    </Component>
  );
};

const Component = styled.div``;

Status.styled = Component;

export { Status };
